"use client";

import {
  DownloadIcon,
  ExternalLinkIcon,
  FileIcon,
  FileTextIcon,
  MaximizeIcon,
  SearchIcon,
  Volume2Icon,
  XIcon,
} from "lucide-react";
import { Dialog } from "radix-ui";
import { useState } from "react";

import type {
  ImageUrlContentBlock,
  MediaContentBlock,
  MediaVariableContentBlock,
  OutputMediaContentBlock,
  WebSearchToolResultContentBlock,
} from "../../types";
import { cn } from "../../utils";
import { CollapsibleSection } from "../CollapsibleSection";

// ---------------------------------------------------------------------------
// Size system
// ---------------------------------------------------------------------------

export type MediaSize = "xs" | "sm" | "md" | "lg";

const sizeStyles = {
  container: {
    xs: "w-fit max-w-20",
    sm: "w-full max-w-xs",
    md: "w-full",
    lg: "w-full",
  },
  image: {
    xs: "h-20 w-20 object-cover",
    sm: "max-h-40 w-auto object-contain",
    md: "max-h-96 w-auto object-contain",
    lg: "max-h-[480px] w-auto object-contain",
  },
  video: {
    xs: "h-20 w-20 object-cover",
    sm: "max-h-40 w-full object-contain",
    md: "max-h-96 w-full object-contain",
    lg: "max-h-[480px] w-full object-contain",
  },
} as const;

// ---------------------------------------------------------------------------
// Shared download helper
// ---------------------------------------------------------------------------

function downloadMedia(url: string, filename: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.target = "_blank";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// ---------------------------------------------------------------------------
// ImageViewer — full-res image in the dialog, centered on black bg
// ---------------------------------------------------------------------------

interface ImageViewerProps {
  src: string;
  alt: string;
  title: string;
  trigger: React.ReactNode;
}

const ImageViewer = ({ src, alt, title, trigger }: ImageViewerProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/80",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "duration-200",
          )}
        />
        <Dialog.Content
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setOpen(false);
            }
          }}
          className={cn(
            "fixed inset-0 z-50 flex items-center justify-center p-4",
            "bg-transparent outline-none",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "duration-200",
          )}
        >
          <div className="flex w-full max-w-[95vw] max-h-[90vh] flex-col overflow-hidden rounded-xl border border-gray-700 bg-gray-900 shadow-2xl">
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-700 bg-gray-800 px-4 py-3">
              <Dialog.Title className="truncate text-base font-semibold text-white">
                {title}
              </Dialog.Title>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => downloadMedia(src, title)}
                  aria-label="Download"
                  className="flex h-8 w-8 items-center justify-center rounded-md text-gray-300 transition-colors hover:bg-gray-700 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500"
                >
                  <DownloadIcon className="h-4 w-4" />
                </button>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    aria-label="Close"
                    className="flex h-8 w-8 items-center justify-center rounded-md text-gray-300 transition-colors hover:bg-gray-700 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                </Dialog.Close>
              </div>
            </div>

            {/* Image */}
            <div className="flex flex-1 items-center justify-center overflow-auto bg-gray-900 p-6">
              <img
                src={src}
                alt={alt}
                className="max-h-[calc(90vh-72px)] max-w-full rounded-lg object-contain shadow-2xl"
              />
            </div>

            <Dialog.Description className="sr-only">
              Fullscreen image viewer
            </Dialog.Description>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

// ---------------------------------------------------------------------------
// VideoViewer
// ---------------------------------------------------------------------------

interface VideoViewerProps {
  src: string;
  title: string;
  trigger: React.ReactNode;
}

const VideoViewer = ({ src, title, trigger }: VideoViewerProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/80",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "duration-200",
          )}
        />
        <Dialog.Content
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setOpen(false);
            }
          }}
          className={cn(
            "fixed inset-0 z-50 flex items-center justify-center p-4",
            "bg-transparent outline-none",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "duration-200",
          )}
        >
          <div className="flex w-full max-w-5xl max-h-[90vh] flex-col overflow-hidden rounded-xl border border-gray-700 bg-gray-900 shadow-2xl">
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-700 bg-gray-800 px-4 py-3">
              <Dialog.Title className="truncate text-base font-semibold text-white">
                {title}
              </Dialog.Title>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => downloadMedia(src, title)}
                  aria-label="Download"
                  className="flex h-8 w-8 items-center justify-center rounded-md text-gray-300 transition-colors hover:bg-gray-700 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500"
                >
                  <DownloadIcon className="h-4 w-4" />
                </button>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    aria-label="Close"
                    className="flex h-8 w-8 items-center justify-center rounded-md text-gray-300 transition-colors hover:bg-gray-700 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                </Dialog.Close>
              </div>
            </div>
            <div className="flex flex-1 items-center justify-center bg-black p-4">
              {}
              <video
                src={src}
                controls
                autoPlay
                className="max-h-[calc(90vh-72px)] w-full rounded-lg"
              />
            </div>
            <Dialog.Description className="sr-only">
              Video player
            </Dialog.Description>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

// ---------------------------------------------------------------------------
// AudioViewer
// ---------------------------------------------------------------------------

interface AudioViewerProps {
  src: string;
  title: string;
  trigger: React.ReactNode;
}

const AudioViewer = ({ src, title, trigger }: AudioViewerProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/60",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "duration-200",
          )}
        />
        <Dialog.Content
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setOpen(false);
            }
          }}
          className={cn(
            "fixed inset-0 z-50 flex items-center justify-center p-4",
            "bg-transparent outline-none",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "duration-200",
          )}
        >
          <div className="flex w-full max-w-lg flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl">
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-100">
                  <Volume2Icon className="h-4 w-4 text-blue-600" />
                </div>
                <Dialog.Title className="truncate text-base font-semibold text-gray-900">
                  {title}
                </Dialog.Title>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => downloadMedia(src, title)}
                  aria-label="Download"
                  className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
                >
                  <DownloadIcon className="h-4 w-4" />
                </button>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    aria-label="Close"
                    className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                </Dialog.Close>
              </div>
            </div>

            {/* Player */}
            <div className="flex flex-col items-center gap-4 px-6 py-8 bg-white">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-blue-50 shadow-inner">
                <Volume2Icon className="h-9 w-9 text-blue-500" />
              </div>
              {}
              <audio controls src={src} className="w-full" autoPlay={false} />
            </div>

            <Dialog.Description className="sr-only">
              Audio player
            </Dialog.Description>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

// ---------------------------------------------------------------------------
// PdfViewer
// ---------------------------------------------------------------------------

interface PdfViewerProps {
  src: string;
  title: string;
  trigger: React.ReactNode;
}

const PdfViewer = ({ src, title, trigger }: PdfViewerProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/60",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "duration-200",
          )}
        />
        <Dialog.Content
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setOpen(false);
            }
          }}
          className={cn(
            "fixed inset-0 z-50 flex items-center justify-center p-4",
            "bg-transparent outline-none",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "duration-200",
          )}
        >
          <div className="flex w-full max-w-5xl max-h-[90vh] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl">
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-100">
                  <FileTextIcon className="h-4 w-4 text-red-600" />
                </div>
                <div className="flex min-w-0 flex-col">
                  <Dialog.Title className="truncate text-base font-semibold text-gray-900">
                    {title}
                  </Dialog.Title>
                  <span className="truncate text-xs text-gray-500 max-w-xs">
                    {src.startsWith("data:") ? "Embedded PDF" : src}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {!src.startsWith("data:") && (
                  <button
                    type="button"
                    onClick={() => downloadMedia(src, title)}
                    aria-label="Download"
                    className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
                  >
                    <DownloadIcon className="h-4 w-4" />
                  </button>
                )}
                <Dialog.Close asChild>
                  <button
                    type="button"
                    aria-label="Close"
                    className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                </Dialog.Close>
              </div>
            </div>

            {/* PDF iframe */}
            <div className="flex-1 overflow-hidden bg-gray-100">
              <iframe
                src={src}
                title={title}
                className="h-[calc(90vh-72px)] w-full border-none"
              />
            </div>

            <Dialog.Description className="sr-only">
              PDF viewer
            </Dialog.Description>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

// ---------------------------------------------------------------------------
// ExpandIcon overlay — shown on hover over thumbnail
// ---------------------------------------------------------------------------

const ExpandOverlay = () => (
  <div
    className={cn(
      "absolute inset-0 flex items-center justify-center",
      "bg-black/0 transition-colors duration-150 hover:bg-black/30",
      "rounded-[inherit]",
    )}
  >
    <MaximizeIcon
      className={cn(
        "h-5 w-5 text-white drop-shadow",
        "opacity-0 transition-opacity duration-150",
        "group-hover/clickable:opacity-100",
      )}
    />
  </div>
);

// ---------------------------------------------------------------------------
// MediaBlock — routes to the correct viewer based on media.type
// ---------------------------------------------------------------------------

interface MediaBlockProps {
  block: MediaContentBlock;
  size?: MediaSize;
}

export const MediaBlock = ({ block, size = "md" }: MediaBlockProps) => {
  const { media } = block;

  // ---- IMAGE ---------------------------------------------------------------
  if (media.type === "image") {
    const thumbnail = (
      <div
        className={cn(
          "group/clickable relative cursor-pointer overflow-hidden rounded-lg border border-zinc-200",
          sizeStyles.container[size],
        )}
      >
        <img
          src={media.url}
          alt={media.title || "Image"}
          className={cn(
            "transition-opacity hover:opacity-90",
            sizeStyles.image[size],
          )}
        />
        <ExpandOverlay />
      </div>
    );

    return (
      <ImageViewer
        src={media.url}
        alt={media.title || "Image"}
        title={media.title || "Image"}
        trigger={thumbnail}
      />
    );
  }

  // ---- AUDIO ---------------------------------------------------------------
  if (media.type === "audio") {
    const trigger = (
      <div
        className={cn(
          "group/clickable relative flex cursor-pointer items-center gap-3 rounded-lg border border-zinc-200 bg-white p-3 transition-colors hover:bg-zinc-50",
          size === "xs" ? "w-fit" : "w-full",
        )}
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-100">
          <Volume2Icon className="h-4 w-4 text-blue-600" />
        </div>
        {size !== "xs" && (
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-medium text-zinc-800">
              {media.title || "Audio"}
            </span>
            <span className="text-xs text-zinc-400">Click to play</span>
          </div>
        )}
        <MaximizeIcon
          className={cn(
            "ml-auto h-3.5 w-3.5 shrink-0 text-zinc-400",
            "opacity-0 transition-opacity group-hover/clickable:opacity-100",
          )}
        />
      </div>
    );

    return (
      <AudioViewer
        src={media.url}
        title={media.title || "Audio"}
        trigger={trigger}
      />
    );
  }

  // ---- VIDEO ---------------------------------------------------------------
  if (media.type === "video") {
    const trigger = (
      <div
        className={cn(
          "group/clickable relative cursor-pointer overflow-hidden rounded-lg border border-zinc-200 bg-black",
          sizeStyles.container[size],
        )}
      >
        {}
        <video
          src={media.url}
          className={cn(sizeStyles.video[size], "pointer-events-none")}
          muted
          playsInline
        />
        <ExpandOverlay />
      </div>
    );

    return (
      <VideoViewer
        src={media.url}
        title={media.title || "Video"}
        trigger={trigger}
      />
    );
  }

  // ---- PDF ----------------------------------------------------------------
  if (media.type === "application/pdf") {
    const trigger = (
      <div
        className={cn(
          "group/clickable flex cursor-pointer items-center gap-3 rounded-lg border border-zinc-200 bg-white p-3 transition-colors hover:bg-zinc-50",
          sizeStyles.container[size],
        )}
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-100">
          <FileTextIcon className="h-4 w-4 text-red-600" />
        </div>
        {size !== "xs" && (
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-medium text-zinc-800">
              {media.title || "PDF"}
            </span>
            <span className="truncate text-xs text-zinc-400">
              {media.url.startsWith("data:") ? "Embedded PDF" : media.url}
            </span>
          </div>
        )}
        <MaximizeIcon
          className={cn(
            "ml-auto h-3.5 w-3.5 shrink-0 text-zinc-400",
            "opacity-0 transition-opacity group-hover/clickable:opacity-100",
          )}
        />
      </div>
    );

    return (
      <PdfViewer
        src={media.url}
        title={media.title || "PDF"}
        trigger={trigger}
      />
    );
  }

  // ---- Generic file fallback -----------------------------------------------
  return (
    <a
      href={media.url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "flex items-center gap-2 rounded-lg border border-zinc-200 p-3 text-xs text-zinc-700 hover:bg-zinc-50",
        sizeStyles.container[size],
      )}
    >
      <FileIcon className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
      <span className="font-medium">{media.title || "File"}</span>
      <ExternalLinkIcon className="ml-auto h-3 w-3 shrink-0 text-zinc-400" />
    </a>
  );
};

// ---------------------------------------------------------------------------
// ImageUrlBlock — renders image_url content blocks
// ---------------------------------------------------------------------------

interface ImageUrlBlockProps {
  block: ImageUrlContentBlock;
  size?: MediaSize;
}

export const ImageUrlBlock = ({ block, size = "md" }: ImageUrlBlockProps) => {
  const alt = block.image_variable || "Image";
  const title = block.image_variable || "Image";

  const thumbnail = (
    <div
      className={cn(
        "group/clickable relative cursor-pointer overflow-hidden rounded-lg border border-zinc-200",
        sizeStyles.container[size],
      )}
    >
      <img
        src={block.image_url.url}
        alt={alt}
        className={cn(
          "transition-opacity hover:opacity-90",
          sizeStyles.image[size],
        )}
      />
      <ExpandOverlay />
    </div>
  );

  return (
    <ImageViewer
      src={block.image_url.url}
      alt={alt}
      title={title}
      trigger={thumbnail}
    />
  );
};

// ---------------------------------------------------------------------------
// OutputMediaBlock — renders generated/output media (e.g. AI-generated images)
// ---------------------------------------------------------------------------

interface OutputMediaBlockProps {
  block: OutputMediaContentBlock;
  size?: MediaSize;
}

const getOutputMediaDisplayUrl = (url?: string, mimeType?: string): string => {
  if (!url) {
    return "";
  }
  if (url.startsWith("data:") || url.startsWith("http")) {
    return url;
  }
  return `data:${mimeType || "image/png"};base64,${url}`;
};

function resolveOutputMediaType(
  mediaType?: string,
  mimeType?: string,
): "image" | "video" | "audio" {
  if (mediaType === "audio" || mediaType === "video" || mediaType === "image") {
    return mediaType;
  }
  if (mimeType?.startsWith("audio/")) {
    return "audio";
  }
  if (mimeType?.startsWith("video/")) {
    return "video";
  }
  return "image";
}

export const OutputMediaBlock = ({
  block,
  size = "md",
}: OutputMediaBlockProps) => {
  const mediaType = resolveOutputMediaType(block.media_type, block.mime_type);
  const src = getOutputMediaDisplayUrl(block.url, block.mime_type);

  if (!src) {
    return null;
  }

  if (mediaType === "audio") {
    const voice = block.provider_metadata?.voice;
    const title =
      block.provider_metadata?.source === "speech"
        ? "Speech Generation"
        : "Audio Generated";
    return (
      <div className="flex w-full flex-col gap-2">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold text-zinc-900">{title}</span>
          {voice && (
            <span className="font-mono text-xs text-zinc-500">
              voice: {voice}
            </span>
          )}
        </div>
        <MediaBlock
          block={{ type: "media", media: { type: "audio", url: src, title } }}
          size={size}
        />
      </div>
    );
  }

  if (mediaType === "video") {
    return (
      <MediaBlock
        block={{
          type: "media",
          media: { type: "video", url: src, title: "Video Generation" },
        }}
        size={size}
      />
    );
  }

  const alt = "Generated image";
  const title = "Generated image";
  const thumbnail = (
    <div
      className={cn(
        "group/clickable relative cursor-pointer overflow-hidden rounded-lg border border-zinc-200",
        sizeStyles.container[size],
      )}
    >
      <img
        src={src}
        alt={alt}
        className={cn(
          "transition-opacity hover:opacity-90",
          sizeStyles.image[size],
        )}
      />
      <ExpandOverlay />
    </div>
  );

  return (
    <div className="flex flex-col gap-2">
      <ImageViewer src={src} alt={alt} title={title} trigger={thumbnail} />
      {block.provider_metadata?.revised_prompt && (
        <p className="text-[10px] italic text-zinc-400">
          {block.provider_metadata.revised_prompt}
        </p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// MediaVariableBlock — renders a media_variable reference badge
// ---------------------------------------------------------------------------

interface MediaVariableBlockProps {
  block: MediaVariableContentBlock;
}

export const MediaVariableBlock = ({ block }: MediaVariableBlockProps) => (
  <span
    className={cn(
      "inline-flex w-fit items-center rounded-full",
      "border border-purple-200 bg-purple-50 px-2 py-0.5",
      "text-xs font-medium text-purple-600",
    )}
  >
    {block.name}
  </span>
);

// ---------------------------------------------------------------------------
// WebSearchResultBlock — collapsible list of web search results
// ---------------------------------------------------------------------------

interface WebSearchResultBlockProps {
  block: WebSearchToolResultContentBlock;
}

export const WebSearchResultBlock = ({ block }: WebSearchResultBlockProps) => {
  const results = block.content.filter(
    (item) => item.type === "web_search_result",
  );
  const preview = `${results.length} result${results.length !== 1 ? "s" : ""} found`;
  const copyContent = results.map((r) => `${r.title}\n${r.url}`).join("\n\n");

  return (
    <CollapsibleSection
      icon={<SearchIcon className="h-3 w-3" />}
      label="Web Search"
      preview={preview}
      copyContent={copyContent}
    >
      <div className="flex flex-col gap-1.5">
        {results.map((result, i) => (
          <a
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            href={result.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group/result flex items-start gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-zinc-100"
          >
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-xs font-medium text-zinc-800 group-hover/result:text-blue-600">
                {result.title}
              </span>
              <span className="truncate text-[10px] text-zinc-400">
                {result.url}
              </span>
            </div>
            <ExternalLinkIcon className="mt-0.5 h-3 w-3 shrink-0 text-zinc-400 group-hover/result:text-blue-500" />
          </a>
        ))}
      </div>
    </CollapsibleSection>
  );
};
